import {headerSizes} from '@commercial-helios/ui/theme'
import type {PageProps} from 'gatsby'
import {graphql} from 'gatsby'
import React from 'react'

import Footer from '@/components/footer'
import Header from '@/components/header'
import SEO from '@/components/seo'
import type {FlexBenefitsQuery} from '@/graphql-types'
import {JobsGrid} from '@/sections/jobs-grid'
import {withPrismicPreview} from '@/utils/with-prismic-preview'
import {Main} from '@helios/shared/components/skip-to-content'
import useMediaQuery from '@helios/shared/hooks/use-media-query'
import type {GatsbyHeadProps} from '@helios/shared/types/gatsby-head'
import {track, TrackingEvent} from '@helios/shared/utils/analytics'

import {BaseBenefits} from './base-benefits'
import {Hero} from './hero'
import {
    Location,
    Office,
    getValuesForLocation,
    getReplacePlaceholders,
    getBaseBenefitsSectionTitle,
    getBaseBenefitsContent,
} from './shared'
import {SlidersAndResults} from './sliders-and-results'
import type {State, Action} from './store'
import {initialState, reducer, ActionType} from './store'

type Props = PageProps<FlexBenefitsQuery>
type Dispatch = React.Dispatch<Action>

const validateInputs = (location: Location | '', office: Office | '') => {
    return (!!location && !!office) || location === Location.REMOTE
}

const FlexBenefits = (props: Props) => {
    const content = props.data.prismicFlexBenefitsPage.data
    const baseBenefitsSectionRef = React.useRef<HTMLDivElement>(null)
    const isSmallScreen = useMediaQuery.max('sm')
    const locations = content.location_select_options[0]

    const [state, dispatch] = React.useReducer(reducer, initialState)

    // Scroll to the base benefits section once the user has selected a location
    React.useEffect(() => {
        const hasFilledInApplicableInputs = validateInputs(state.location, state.office)

        if (!hasFilledInApplicableInputs || !baseBenefitsSectionRef.current) {
            return
        }

        const baseBenefitsTopY =
            window.pageYOffset + baseBenefitsSectionRef.current.getBoundingClientRect().top
        const offset = isSmallScreen ? headerSizes.heightSmall : headerSizes.heightLarge

        document.documentElement.scrollTo({
            top: baseBenefitsTopY - offset,
            behavior: 'smooth',
        })

        track(TrackingEvent.FlexBenefitsBaseBenefitsViewed)
    }, [state.location, state.office, isSmallScreen])

    return (
        <>
            <Header />

            <Main>
                <Hero
                    title={content.hero[0].title}
                    image={content.hero[0].image}
                    content={content.hero[0].content.richText}
                    locationSelect={{
                        placeholder: content.location_select_placeholder,
                        label: content.location_select_label,
                        options: [
                            {
                                label: locations.denmark,
                                value: Location.DENMARK,
                            },
                            {
                                label: locations.sweden,
                                value: Location.SWEDEN,
                            },
                            {
                                label: locations.germany,
                                value: Location.GERMANY,
                            },
                            {
                                label: locations.united_kingdom,
                                value: Location.UK,
                            },
                            {
                                label: locations.spain,
                                value: Location.SPAIN,
                            },
                            {
                                label: locations.portugal,
                                value: Location.PORTUGAL,
                            },
                            {
                                label: locations.quebec,
                                value: Location.QUEBEC,
                            },
                            {
                                label: locations.remote,
                                value: Location.REMOTE,
                            },
                        ],
                        value: state.location,
                        onChange: (location) => {
                            dispatch({
                                type: ActionType.LOCATION_UPDATED,
                                location,
                            })
                            track(TrackingEvent.FlexBenefitsLocationChange, {
                                action: 'changed',
                                location,
                            })
                        },
                    }}
                    officeRadioGroup={{
                        legend: content.office_radio_group_legend,
                        options: [
                            {
                                ...content.office_radio_option[0],
                                value: Office.IN_OFFICE,
                                disabled: state.location === Location.REMOTE,
                            },
                            {
                                ...content.remote_radio_option[0],
                                value: Office.REMOTE,
                                disabled: state.location === Location.REMOTE,
                            },
                        ],
                        value: state.office,
                        onChange: (office) => {
                            dispatch({
                                type: ActionType.OFFICE_UPDATED,
                                office,
                            })

                            track(TrackingEvent.FlexBenefitsOfficeChange, {
                                action: 'changed',
                                office,
                            })
                        },
                    }}
                />

                {/* Only show these sections once the user has selected a location */}
                {validateInputs(state.location, state.office) && (
                    <DynamicSections
                        state={{
                            ...state,
                            location: state.location as Location,
                            office: state.office,
                        }}
                        dispatch={dispatch}
                        content={content}
                        ref={baseBenefitsSectionRef}
                    />
                )}
            </Main>

            <Footer />
        </>
    )
}

export const Head = ({location, pageContext, data}: GatsbyHeadProps<FlexBenefitsQuery>) => {
    const pageData = data.prismicFlexBenefitsPage.data

    return (
        <SEO
            location={location}
            pageContext={pageContext}
            title={pageData.meta_title}
            description={pageData.meta_description}
            openGraphImageUrl={pageData.open_graph_image?.url}
        />
    )
}

type StateWithSelectedOptions = State & {
    location: Location
    office: Office | ''
}

type DynamicSectionsProps = {
    state: StateWithSelectedOptions
    content: FlexBenefitsQuery['prismicFlexBenefitsPage']['data']
    dispatch: Dispatch
}

const DynamicSections = React.forwardRef<HTMLDivElement, DynamicSectionsProps>(
    ({state, dispatch, content}, ref) => {
        const {budgetTotal, healthcareValue, lunchValue, costPerLeaveDay, maxAdditionalLeaveDays} =
            getValuesForLocation(state.location)
        const locationNames = content.location_names[0]

        const budgetUsed = [
            state.healthcareAndWellbeing,
            state.leaveDays * costPerLeaveDay,
            state.charityAndDonations,
            state.homeOfficeSetup,
            state.learningAndDevelopment,
            state.artsCultureEntertainment,
            state.lifestyleTravelAndTransport,
            state.yourFamily,
        ].reduce((sum, cur) => sum + cur, 0)

        const replacePlaceholders = getReplacePlaceholders({
            location: state.location,
            budgetTotal,
            budgetUsed,
            locationNames,
        })

        const baseBenefitsTitle = getBaseBenefitsSectionTitle(
            state.location,
            state.office,
            replacePlaceholders(content.base_benefits_section_country_title).join(''),
            replacePlaceholders(content.base_benefits_section_partial_remote_title).join(''),
            content.base_benefits_section_fully_remote_title
        )

        const baseBenefitsFeatures = getBaseBenefitsContent(state.location, state.office, content)

        type SliderActionTypes =
            | ActionType.ARTS_CULTURE_ENTERTAINMENT_UPDATED
            | ActionType.CHARITY_AND_DONATIONS_UPDATED
            | ActionType.HEALTHCARE_AND_WELLBEING_UPDATED
            | ActionType.HOME_OFFICE_SETUP_UPDATED
            | ActionType.LEARNING_AND_DEVELOPMENT_UPDATED
            | ActionType.LIFESTYLE_TRAVEL_AND_TRANSPORT_UPDATED
            | ActionType.YOUR_FAMILY_UPDATED
            | ActionType.LEAVE_DAYS_UPDATED

        const handleSliderChange = (
            value: number,
            sliderBudget: number,
            actionType: SliderActionTypes
        ) => {
            // If used budget goes over total budget, set value to the
            // max possible budget available for that slider
            if (budgetUsed > budgetTotal) {
                const excludingBudget = budgetUsed - sliderBudget
                value = budgetTotal - excludingBudget
            }

            // If used budget is greater than total budget and
            // user is trying to increase, return out of change handler.
            // Otherwise, allow Dispatch, as we allow users to decrease value
            if (budgetTotal <= budgetUsed && sliderBudget < value) {
                return
            }

            dispatch({
                type: actionType,
                value,
            })
        }

        const jobsGridLinkTracking = () =>
            track(TrackingEvent.FlexBenefitsOpenRolesLinkActioned, {
                action: 'completed',
            })

        return (
            <div ref={ref}>
                <BaseBenefits
                    title={baseBenefitsTitle}
                    items={baseBenefitsFeatures.map((feature) => ({
                        title: feature.title,
                        body: feature.description.richText,
                        image: feature.image,
                    }))}
                />

                <SlidersAndResults
                    title={content.sliders_title}
                    subtitle={content.sliders_subtitle}
                    sliders={[
                        {
                            icons: [
                                content.health_and_wellbeing_slider[0].low_icon,
                                content.health_and_wellbeing_slider[0].medium_icon,
                                content.health_and_wellbeing_slider[0].high_icon,
                            ],
                            heading: content.health_and_wellbeing_slider[0].heading,
                            label: content.health_and_wellbeing_slider[0].label,
                            min: 0,
                            max: budgetTotal,
                            value: state.healthcareAndWellbeing,
                            disabled:
                                budgetUsed >= budgetTotal && state.healthcareAndWellbeing === 0,
                            onFocus: () => {
                                track(TrackingEvent.FlexBenefitsSliderActioned, {
                                    action: 'interacted',
                                    slider: 'healthcareAndWellbeingSlider',
                                })
                            },
                            onChange: (value) =>
                                handleSliderChange(
                                    value,
                                    state.healthcareAndWellbeing,
                                    ActionType.HEALTHCARE_AND_WELLBEING_UPDATED
                                ),
                        },
                        {
                            icons: [
                                content.leave_days_slider[0].low_icon,
                                content.leave_days_slider[0].medium_icon,
                                content.leave_days_slider[0].high_icon,
                            ],
                            heading: content.leave_days_slider[0].heading,
                            label: content.leave_days_slider[0].label,
                            min: 0,
                            max: maxAdditionalLeaveDays,
                            value: state.leaveDays,
                            disabled: budgetUsed >= budgetTotal && state.leaveDays === 0,
                            onFocus: () => {
                                track(TrackingEvent.FlexBenefitsSliderActioned, {
                                    action: 'interacted',
                                    slider: 'leaveDaysSlider',
                                })
                            },
                            onChange: (value) => {
                                // If the remaining budget plus cost of a leave day is greater than
                                // available budget, and the user is trying to increase the number of
                                // days, exit the function
                                if (
                                    budgetUsed + costPerLeaveDay > budgetTotal &&
                                    state.leaveDays < value
                                ) {
                                    return
                                }

                                dispatch({
                                    type: ActionType.LEAVE_DAYS_UPDATED,
                                    value,
                                })
                            },
                        },
                        {
                            icons: [
                                content.charity_and_donations_slider[0].low_icon,
                                content.charity_and_donations_slider[0].medium_icon,
                                content.charity_and_donations_slider[0].high_icon,
                            ],
                            heading: content.charity_and_donations_slider[0].heading,
                            label: content.charity_and_donations_slider[0].label,
                            min: 0,
                            max: budgetTotal,
                            value: state.charityAndDonations,
                            disabled: budgetUsed >= budgetTotal && state.charityAndDonations === 0,
                            onFocus: () => {
                                track(TrackingEvent.FlexBenefitsSliderActioned, {
                                    action: 'interacted',
                                    slider: 'charityAndDonationsSlider',
                                })
                            },
                            onChange: (value) =>
                                handleSliderChange(
                                    value,
                                    state.charityAndDonations,
                                    ActionType.CHARITY_AND_DONATIONS_UPDATED
                                ),
                        },
                        {
                            icons: [
                                content.home_office_setup_slider[0].low_icon,
                                content.home_office_setup_slider[0].medium_icon,
                                content.home_office_setup_slider[0].high_icon,
                            ],
                            heading: content.home_office_setup_slider[0].heading,
                            label: content.home_office_setup_slider[0].label,
                            min: 0,
                            max: budgetTotal,
                            value: state.homeOfficeSetup,
                            disabled: budgetUsed >= budgetTotal && state.homeOfficeSetup === 0,
                            onFocus: () => {
                                track(TrackingEvent.FlexBenefitsSliderActioned, {
                                    action: 'interacted',
                                    slider: 'homeOfficeSetupSlider',
                                })
                            },
                            onChange: (value) =>
                                handleSliderChange(
                                    value,
                                    state.homeOfficeSetup,
                                    ActionType.HOME_OFFICE_SETUP_UPDATED
                                ),
                        },
                        {
                            icons: [
                                content.learning_and_development_slider[0].low_icon,
                                content.learning_and_development_slider[0].medium_icon,
                                content.learning_and_development_slider[0].high_icon,
                            ],
                            heading: content.learning_and_development_slider[0].heading,
                            label: content.learning_and_development_slider[0].label,
                            min: 0,
                            max: budgetTotal,
                            value: state.learningAndDevelopment,
                            disabled:
                                budgetUsed >= budgetTotal && state.learningAndDevelopment === 0,
                            onFocus: () => {
                                track(TrackingEvent.FlexBenefitsSliderActioned, {
                                    action: 'interacted',
                                    slider: 'learningAndDevelopmentSlider',
                                })
                            },
                            onChange: (value) =>
                                handleSliderChange(
                                    value,
                                    state.learningAndDevelopment,
                                    ActionType.LEARNING_AND_DEVELOPMENT_UPDATED
                                ),
                        },
                        {
                            icons: [
                                content.arts_culture_and_entertainment_slider[0].low_icon,
                                content.arts_culture_and_entertainment_slider[0].medium_icon,
                                content.arts_culture_and_entertainment_slider[0].high_icon,
                            ],
                            heading: content.arts_culture_and_entertainment_slider[0].heading,
                            label: content.arts_culture_and_entertainment_slider[0].label,
                            min: 0,
                            max: budgetTotal,
                            value: state.artsCultureEntertainment,
                            disabled:
                                budgetUsed >= budgetTotal && state.artsCultureEntertainment === 0,
                            onFocus: () => {
                                track(TrackingEvent.FlexBenefitsSliderActioned, {
                                    action: 'interacted',
                                    slider: 'artsCultureEntertainmentSlider',
                                })
                            },
                            onChange: (value) =>
                                handleSliderChange(
                                    value,
                                    state.artsCultureEntertainment,
                                    ActionType.ARTS_CULTURE_ENTERTAINMENT_UPDATED
                                ),
                        },
                        {
                            icons: [
                                content.lifestyle_travel_and_transport_slider[0].low_icon,
                                content.lifestyle_travel_and_transport_slider[0].medium_icon,
                                content.lifestyle_travel_and_transport_slider[0].high_icon,
                            ],
                            heading: content.lifestyle_travel_and_transport_slider[0].heading,
                            label: content.lifestyle_travel_and_transport_slider[0].label,
                            min: 0,
                            max: budgetTotal,
                            value: state.lifestyleTravelAndTransport,
                            disabled:
                                budgetUsed >= budgetTotal &&
                                state.lifestyleTravelAndTransport === 0,
                            onFocus: () => {
                                track(TrackingEvent.FlexBenefitsSliderActioned, {
                                    action: 'interacted',
                                    slider: 'lifestyleTravelAndTransportSlider',
                                })
                            },
                            onChange: (value) =>
                                handleSliderChange(
                                    value,
                                    state.lifestyleTravelAndTransport,
                                    ActionType.LIFESTYLE_TRAVEL_AND_TRANSPORT_UPDATED
                                ),
                        },
                        {
                            icons: [
                                content.your_family_slider[0].low_icon,
                                content.your_family_slider[0].medium_icon,
                                content.your_family_slider[0].high_icon,
                            ],
                            heading: content.your_family_slider[0].heading,
                            label: content.your_family_slider[0].label,
                            min: 0,
                            max: budgetTotal,
                            value: state.yourFamily,
                            disabled: budgetUsed >= budgetTotal && state.yourFamily === 0,
                            onFocus: () => {
                                track(TrackingEvent.FlexBenefitsSliderActioned, {
                                    action: 'interacted',
                                    slider: 'yourFamilySlider',
                                })
                            },
                            onChange: (value) =>
                                handleSliderChange(
                                    value,
                                    state.yourFamily,
                                    ActionType.YOUR_FAMILY_UPDATED
                                ),
                        },
                    ]}
                    budgetTotal={budgetTotal}
                    budgetUsed={budgetUsed}
                    location={state.location}
                    showResultsText={content.show_results_text}
                    hideResultsText={content.hide_results_text}
                    resultsTitle={content.results_title}
                    resultsBudgetAllowanceText={content.results_budget_allowance_text}
                    resultsPackageTitle={content.results_package_title}
                    resultsProgressBarText={content.results_progress_bar_text}
                    resultsPackageItems={[
                        {
                            title: content.results_package_items[0].private_healthcare,
                            value: healthcareValue,
                        },
                        {
                            title: content.results_package_items[0].lunch,
                            value: lunchValue,
                        },
                        {
                            title: content.results_package_items[0].flex_benefits_budget,
                            value: budgetUsed,
                        },
                    ]}
                    resultsTotalBenefitsTitle={content.results_total_benefits_title}
                    resultsNote={content.results_note}
                    locationNames={locationNames}
                />

                <JobsGrid
                    headline={content.job_grid[0].title}
                    text={content.job_grid[0].description}
                    ctaText={content.job_grid[0].cta_text}
                    linkTracking={jobsGridLinkTracking}
                />
            </div>
        )
    }
)

export const query = graphql`
    query FlexBenefits($locale: String!) {
        prismicFlexBenefitsPage(lang: {eq: $locale}) {
            _previewable
            data {
                meta_title
                meta_description
                open_graph_image {
                    url
                }

                hero {
                    title
                    content {
                        richText
                    }
                    image {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                }

                location_select_label
                location_select_placeholder

                location_select_options {
                    denmark
                    sweden
                    germany
                    united_kingdom
                    spain
                    portugal
                    quebec
                    remote
                }

                sliders_title
                sliders_subtitle
                show_results_text
                hide_results_text
                results_title
                results_budget_allowance_text
                results_package_title
                results_progress_bar_text
                results_total_benefits_title
                results_note
                base_benefits_section_country_title
                base_benefits_section_partial_remote_title
                base_benefits_section_fully_remote_title

                health_and_wellbeing_slider {
                    heading
                    label
                    low_icon {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    medium_icon {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    high_icon {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                }

                leave_days_slider {
                    heading
                    label
                    low_icon {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    medium_icon {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    high_icon {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                }

                charity_and_donations_slider {
                    heading
                    label
                    low_icon {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    medium_icon {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    high_icon {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                }

                home_office_setup_slider {
                    heading
                    label
                    low_icon {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    medium_icon {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    high_icon {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                }

                learning_and_development_slider {
                    heading
                    label
                    low_icon {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    medium_icon {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    high_icon {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                }

                arts_culture_and_entertainment_slider {
                    heading
                    label
                    low_icon {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    medium_icon {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    high_icon {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                }

                lifestyle_travel_and_transport_slider {
                    heading
                    label
                    low_icon {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    medium_icon {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    high_icon {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                }

                your_family_slider {
                    heading
                    label
                    low_icon {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    medium_icon {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    high_icon {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                }

                job_grid {
                    title
                    description
                    cta_text
                }

                office_radio_group_legend

                office_radio_option {
                    icon {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    title
                    description
                }

                remote_radio_option {
                    icon {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    title
                    description
                }

                results_package_items {
                    private_healthcare
                    lunch
                    flex_benefits_budget
                }

                base_benefits {
                    image {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    title
                    description {
                        richText
                    }
                }

                base_benefits_germany {
                    image {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    title
                    description {
                        richText
                    }
                }

                base_benefits_germany_remote {
                    image {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    title
                    description {
                        richText
                    }
                }

                base_benefits_remote {
                    image {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    title
                    description {
                        richText
                    }
                }

                ways_of_working {
                    image {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    title
                    description {
                        richText
                    }
                }

                ways_of_working_remote {
                    image {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    title
                    description {
                        richText
                    }
                }

                parental_benefits {
                    image {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    title
                    description {
                        richText
                    }
                }

                parental_benefits_spain {
                    image {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    title
                    description {
                        richText
                    }
                }

                location_names {
                    denmark
                    sweden
                    germany
                    uk
                    portugal
                    spain
                    quebec
                    remote
                }
            }
        }
    }
`

export default withPrismicPreview(FlexBenefits)
